import { translate } from 'i18n/i18n'
import React from 'react'
import { checkAuthorization } from 'utils/checkAuthorization'
import { getIntercomPrePopulatedMessage } from 'utils/getIntercomPrePopulatedMessage'
import { SharedSettings } from '../../../settings/SharedSettings'
import { MenuButton } from '../ActionMenu/MenuButton/MenuButton'
import { openNodeDeviceModal } from '../DevicesManagement/NodeDeviceModal'
import { checkIsTrapConsumableUpdatable, openNodeConsumableModal } from '../NodeConsumableModal'
import { openNodeMaintenanceModal } from '../NodeMaintenanceModal/NodeMaintenanceModal'
import { openNodeRemoveModal } from '../NodeRemoveModal/NodeRemoveModal'
import { openNodeSwapModal } from '../NodeSwapModal/NodeSwapModal'
import type { TActiveGateway, TActiveNode, TPlannedGateway, TPlannedNode } from '../types'
import { checkRepositionPermissions } from './checkRepositionPermissions'
import { isDeviceableNode, isLocationSensitiveNode, isTrapNode } from './constants/nodeType'
import { isSemiosGatewayType } from './getNodeNetworkType'
import { getNodeTypeLabel } from './getNodeTypeLabel'
import { isPlannedNode } from './isPlannedNode'
import { openNodeRepositionModal } from './openNodeRepositionModal'
import { showModalWithCachedCheckbox } from './showModalWithCachedCheckbox'

export const getNodeActions = (
  node: TActiveNode | TActiveGateway | TPlannedNode | TPlannedGateway,
  connected: boolean,
) => {
  const equipmentTypeLabel = getNodeTypeLabel(
    (node as TActiveNode).nodeType || (node as TActiveGateway).gatewayType,
  )

  const prePopulatedMessage = getIntercomPrePopulatedMessage({ node: node }) ?? ''
  const isGateway = !!(node as TActiveGateway).gatewayType

  const checkNodeRemovePermission = () => {
    if (!checkAuthorization({ permission: 'VIEW_SSC_REMOVE_NODE_REASONS', entity: 'FOR_ANY_ENTITY' }))
      return false

    if (isGateway) {
      return checkAuthorization({
        permission: 'EDIT_SSC_REMOVE_GATEWAY',
        entity: (node as TActiveGateway).propertyIds[0],
      })
    } else {
      return checkAuthorization({
        permission: 'EDIT_SSC_REMOVE_NODE',
        entity: (node as TActiveNode).propertyId,
      })
    }
  }

  const handleClickReposition = () => {
    const key = 'repositionNodeWarning'

    if (
      !localStorage.getItem(key) &&
      !isPlannedNode(node) &&
      (node as TActiveNode)?.nodeType &&
      isLocationSensitiveNode((node as TActiveNode).nodeType)
    ) {
      showModalWithCachedCheckbox({
        title: 'Reposition Equipment',
        DescriptionElement: (
          <div>
            {translate.phrases.placeholder(
              'Changing the Device Location after install may result in data loss.',
            )}
          </div>
        ),
        key,
        onConfirm: () => openNodeRepositionModal(node),
      })

      return
    }

    openNodeRepositionModal(node)
  }

  return (
    <>
      {!isPlannedNode(node) && isDeviceableNode((node as TActiveNode).nodeType) && (
        <MenuButton
          key={'devices-management'}
          label={translate.phrases.placeholder('Devices Management')}
          description={translate.phrases.placeholder('Manage Devices')}
          onClick={() => openNodeDeviceModal((node as TActiveNode).nodeIdentifier)}
        />
      )}
      {!isPlannedNode(node) && (
        <MenuButton
          key={'maintenance'}
          label={translate.phrases.placeholder('{{equipmentType}} Maintenance', {
            equipmentType: equipmentTypeLabel,
          })}
          description={translate.phrases.placeholder('View maintenance actions')}
          onClick={() => openNodeMaintenanceModal(node as TActiveNode | TActiveGateway)}
        />
      )}

      <MenuButton
        key={'reposition'}
        label={translate.phrases.placeholder('Reposition', {
          equipmentType: equipmentTypeLabel,
        })}
        description={translate.phrases.placeholder('Reposition equipment')}
        onClick={handleClickReposition}
        disabled={!checkRepositionPermissions(node)}
      />

      {isTrapNode((node as TActiveNode | TPlannedNode)?.nodeType) && !isPlannedNode(node) && (
        <MenuButton
          key={'trap-consumables'}
          label={translate.phrases.placeholder('Manage Trap Consumables', {
            equipmentType: equipmentTypeLabel,
          })}
          disabled={!checkIsTrapConsumableUpdatable(node as TActiveNode)}
          description={translate.phrases.placeholder('Manage lures, liners, coroplasts and battery')}
          onClick={() => openNodeConsumableModal(node as TActiveNode)}
        />
      )}

      {!isPlannedNode(node) && !isSemiosGatewayType(node as TActiveGateway | TActiveNode) && (
        <MenuButton
          key={'swap'}
          label={translate.phrases.placeholder('Swap Equipment')}
          description={translate.phrases.placeholder('Swap {{equipmentType}}', {
            equipmentType: equipmentTypeLabel,
          })}
          onClick={() => openNodeSwapModal(node as TActiveNode)}
          disabled={
            !checkAuthorization({
              permission: 'EDIT_SSC_SWAP_NODE',
              entity: (node as TActiveNode).propertyId,
            }) || !checkAuthorization({ permission: 'VIEW_SSC_SWAP_NODE_REASONS', entity: 'FOR_ANY_ENTITY' })
          }
        />
      )}

      {!isPlannedNode(node) && (
        <MenuButton
          key={'remove'}
          label={translate.phrases.placeholder('Remove Equipment', {
            equipmentType: equipmentTypeLabel,
          })}
          disabled={!checkNodeRemovePermission()}
          description={translate.phrases.placeholder('Remove {{equipmentType}}', {
            equipmentType: equipmentTypeLabel,
          })}
          onClick={() => openNodeRemoveModal(node as TActiveNode)} //TODO: Fix this, do we allow to remove a gateway?
        />
      )}

      <MenuButton
        key={'guides'}
        label={translate.phrases.placeholder('Guides and Resources')}
        description={translate.phrases.placeholder('Access step-by-step instructions')}
        onClick={() => window.open(SharedSettings.SSC_INSTALLATION_GUIDES_URL, '_blank')}
      />
      <MenuButton
        key={'contact-support'}
        label={translate.phrases.placeholder('Contact Support')}
        description={translate.phrases.placeholder('Reach out to our team for help')}
        disabled={!connected}
        onClick={() => {
          // @ts-expect-error
          window.Intercom('showNewMessage', prePopulatedMessage)
        }}
      />
    </>
  )
}
